// libraries
import { useMemo } from 'react'
import Big from 'big.js'
import { Typography, Stack, Divider } from '@mui/material'
import { useFormContext } from 'react-hook-form'

// generic components
import Input from 'components/generic/form/Input'
import SkeletonWrapper from 'components/generic/SkeletonWrapper'

// helpers and utils
import { setDecimalPrecision } from 'utils/helpers'
import { ROUNDING_PRECISIONS } from 'utils/constants'

interface BatchBillFooterProps {
  billTotalValues: {
    [key: string]: number
  }
  pricePrepend: string
  isLoading: boolean
}

const BatchBillFooter: React.FC<BatchBillFooterProps> = ({
  billTotalValues,
  pricePrepend = '$',
  isLoading = false,
}) => {
  const { control, formState } = useFormContext()
  const { errors } = formState

  const totalValue = useMemo(() => {
    return Object.values(billTotalValues).reduce(
      (aggregate: number, billTotalValue: number) =>
        Big(aggregate).plus(Big(billTotalValue)).round(ROUNDING_PRECISIONS.COST).toNumber(),
      0,
    )
  }, [billTotalValues])

  return (
    <Stack bottom={0} position="sticky" width="100%" sx={{ backgroundColor: 'background.default' }}>
      <Divider flexItem />
      <Stack direction="row" p={2} alignItems="center">
        <SkeletonWrapper isLoading={isLoading}>
          <Input
            name="notes"
            label="Batch Bill Notes"
            variant="outlined"
            defaultValue=""
            control={control}
            error={!!errors.notes}
            helperText={errors.notes?.message}
          />
        </SkeletonWrapper>
        <Stack mx={3}>
          <SkeletonWrapper isLoading={isLoading}>
            <Typography color="textSecondary">Total</Typography>
            <Typography align="left" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
              {`${pricePrepend}${setDecimalPrecision(totalValue, { decimalPrecisionOverride: 2 })}`}
            </Typography>
          </SkeletonWrapper>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default BatchBillFooter
